import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { HardDrive } from "lucide-react";

function _fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function _relTime(iso) {
  if (!iso) return "never";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  if (d >= 1) return `${d}d ${h}h ago`;
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 1) return `${h}h ${m}m ago`;
  return `${m}m ago`;
}

function _healthTier(iso) {
  if (!iso) return { label: "No backups", tone: "rose" };
  const ageDays = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (ageDays <= 8) return { label: "Healthy", tone: "emerald" };
  if (ageDays <= 15) return { label: "Stale", tone: "amber" };
  return { label: "Overdue", tone: "rose" };
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [backup, setBackup] = useState(null);
  useEffect(() => { api.get("/dashboard/admin").then((r) => setData(r.data)); }, []);
  useEffect(() => {
    api.get("/admin/backup/archive")
      .then((r) => setBackup(r.data))
      .catch(() => setBackup({ backups: [], retention_weeks: 8 }));
  }, []);
  if (!data) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Admin Control · MST</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">Operations Overview</h1>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Pay Period {data.pay_period.period_number}</div>
          <div className="text-sm font-medium">{data.pay_period.range_label}</div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Total Hours</div>
          <div className="font-mono text-4xl mt-2 tracking-tighter" data-testid="kpi-total-hours">{data.total_hours}</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Clocked-in Now</div>
          <div className="font-mono text-4xl mt-2 tracking-tighter">{data.active_clock_ins}</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Users</div>
          <div className="font-mono text-4xl mt-2 tracking-tighter">{data.total_users}</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Per Diems</div>
          <div className="mt-2 flex items-end gap-3">
            <div><div className="font-mono text-2xl">{data.per_diems.cdn_users}</div><div className="text-[10px] uppercase tracking-widest text-gray-500">CDN ${data.per_diems.rates.CDN}</div></div>
            <div><div className="font-mono text-2xl">{data.per_diems.int_users}</div><div className="text-[10px] uppercase tracking-widest text-gray-500">INT ${data.per_diems.rates.INT}</div></div>
          </div>
        </div>
      </div>

      {/* Backup health — compact status strip under KPIs */}
      <BackupHealthTile backup={backup} />


      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <div className="border border-summit-hairline rounded-lg bg-white">
          <div className="px-5 py-4 border-b border-summit-hairline">
            <h2 className="font-display text-lg font-medium tracking-tight">By aircraft</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-summit-hairline">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Tail</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {data.by_aircraft.length === 0 && (<tr><td colSpan="2" className="px-4 py-6 text-center text-gray-400 text-sm">No data</td></tr>)}
              {data.by_aircraft.map((a) => (
                <tr key={a.tail} className="border-b border-summit-hairline last:border-0">
                  <td className="px-4 py-2 font-mono">{a.tail}</td>
                  <td className="px-4 py-2 font-mono text-right">{a.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-summit-hairline rounded-lg bg-white">
          <div className="px-5 py-4 border-b border-summit-hairline">
            <h2 className="font-display text-lg font-medium tracking-tight">By engineer</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-summit-hairline">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {data.by_user.length === 0 && (<tr><td colSpan="2" className="px-4 py-6 text-center text-gray-400 text-sm">No data</td></tr>)}
              {data.by_user.map((u) => (
                <tr key={u.name} className="border-b border-summit-hairline last:border-0">
                  <td className="px-4 py-2">{u.name}</td>
                  <td className="px-4 py-2 font-mono text-right">{u.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BackupHealthTile({ backup }) {
  if (!backup) return null;
  const backups = backup.backups || [];
  const newest = backups[0] || null;
  const totalBytes = backups.reduce((s, b) => s + (b.size_bytes || 0), 0);
  const tier = _healthTier(newest?.mtime_iso);
  const toneClass = tier.tone === "emerald"
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : tier.tone === "amber"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-rose-50 text-rose-800 border-rose-200";
  return (
    <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-5" data-testid="backup-health-tile">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-summit-glacier-soft border border-summit-hairline">
            <HardDrive className="w-4 h-4 text-summit-primary" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Backup health</div>
            <div className="flex items-baseline gap-3 mt-1 flex-wrap">
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${toneClass}`} data-testid="backup-health-status">
                {tier.label}
              </span>
              <span className="text-sm text-gray-700" data-testid="backup-health-last-run">
                Last backup {_relTime(newest?.mtime_iso)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Archives</div>
            <div className="font-mono text-2xl tracking-tighter" data-testid="backup-health-count">{backups.length}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Total size</div>
            <div className="font-mono text-2xl tracking-tighter" data-testid="backup-health-size">{_fmtBytes(totalBytes)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Next run</div>
            <div className="text-sm font-medium" data-testid="backup-health-next">{backup.next_run_mst || "Sundays 03:00 MST"}</div>
          </div>
          <Link
            to="/admin/settings"
            className="text-sm underline text-summit-primary hover:no-underline whitespace-nowrap"
            data-testid="backup-health-manage"
          >
            Manage →
          </Link>
        </div>
      </div>
    </div>
  );
}
